import { tipTapBaseExtensions } from '@lessonup/pins-shared';
import {
  borderRadius,
  Button,
  color,
  css,
  font,
  fontSize,
  IconCopy,
  lineHeight,
  spacing,
  styled,
  useTranslation,
} from '@lessonup/ui-components';
import { Heading } from '@tiptap/extension-heading';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { EditorContent, JSONContent, useEditor } from '@tiptap/react';
import React, { FC, RefObject, useEffect, useImperativeHandle, useState } from 'react';
import { i18nextNamespaceAiTools } from '../AiToolbox.utils';
import { useAiToolboxContext } from '../context/AiToolboxContext';

interface TipTapEditorProps {
  html?: string;
  onCopy?: (item: ClipboardItem) => void;
  saveButtons?: React.ReactNode;
  showButtons?: boolean;
  ref?: RefObject<TipTapEditorRef | null>;
}

export interface TipTapEditorRef {
  getContent: () => string;
  getProsemirrorContent: () => JSONContent;
}

export const TipTapEditor: FC<TipTapEditorProps> = ({
  html,
  onCopy,
  saveButtons: buttons,
  ref,
  showButtons = true,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const { showInline } = useAiToolboxContext();
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const editor = useEditor({
    extensions: [...tipTapBaseExtensions, ...(showInline ? [] : [Heading]), Table, TableCell, TableHeader, TableRow],
    content: html,
    editable: false,
    immediatelyRender: false,
  });

  useEffect(() => {
    if (editor && html) {
      editor.commands.setContent(html);
    }
  }, [editor, html]);

  useImperativeHandle(ref, () => ({
    getContent: () => {
      if (!editor) return '';
      return editor.getHTML();
    },
    getProsemirrorContent: () => {
      if (!editor) return {};
      return editor.getJSON();
    },
  }));

  const handleCopy = async () => {
    if (!editor) return;

    const htmlContent = editor.getHTML();
    const plainContent = editor.getText();

    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([htmlContent], { type: 'text/html' }),
      'text/plain': new Blob([plainContent], { type: 'text/plain' }),
    });

    onCopy?.(clipboardItem);

    await navigator.clipboard.write([clipboardItem]);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <StyledWrapper>
      <StyledEditorContentWrapper showInline={showInline ?? false}>
        <StyledEditorContent editor={editor} />
      </StyledEditorContentWrapper>
      {showButtons && (
        <StyledButtonWrapper>
          <Button onClick={handleCopy} buttonType="neutral" iconStart={<IconCopy />} showStroke>
            {isCopied ? t('copiedOutput') : t('copyOutput')}
          </Button>

          {buttons}
        </StyledButtonWrapper>
      )}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  position: relative;

  table {
    border-collapse: collapse;
    width: 100%;
    margin: ${spacing.size16} 0;
  }

  th,
  td {
    border: 1px solid ${color.neutral.surface1.text};
    padding: ${spacing.size8};
  }

  th {
    background-color: ${color.neutral.surface1.background};
    font-weight: 600;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p {
    margin: 0; // reset
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: ${font.heading.cssValue};
    font-style: normal;
    font-weight: 800;
  }

  h1 {
    font-size: ${fontSize.headlineSizeMedium};
    line-height: ${lineHeight.headlineSizeMedium};
    margin-bottom: ${spacing.size16};
  }

  h2 {
    font-size: ${fontSize.headlineSizeSmall};
    line-height: ${lineHeight.headlineSizeSmall};
    margin-bottom: ${spacing.size8};
  }

  p {
    font-size: ${fontSize.bodyTextSizeMedium};
    line-height: ${lineHeight.bodyTextSizeMedium};

    + p,
    + h2,
    + h3,
    + h4,
    + h5,
    + h6 {
      margin-top: ${spacing.size16};
    }
  }
`;

const StyledEditorContent = styled(EditorContent)`
  overflow-x: auto;

  @media print {
    overflow-x: visible;
  }
`;

const StyledEditorContentWrapper = styled.div<{ showInline: boolean }>`
  ${({ showInline }) =>
    showInline &&
    css`
      border-radius: ${borderRadius.rounded4};
      margin-top: ${spacing.size16};
      padding: ${spacing.size8};
      background: ${color.neutral.surface1.background};
    `}
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${spacing.size16};
  gap: ${spacing.size8};

  @media print {
    display: none;
  }
`;
